<!--
  Базовая шапка страницы с заголовком, возвратом и действиями.

  Используй BaseNav на страницах списка и деталей вместо локальных header-блоков.
  Компонент сам выравнивает заголовок и действия на десктопе, а на мобильном
  распределяет action-кнопки по две в строке.

  Действия передавай в default slot напрямую, без локального контейнера:
  <BaseNav title="Заказы">
    <BaseButton @click="exportOrders">Экспорт</BaseButton>
    <BasePrimaryButton icon="addSquare" @click="createOrder">
      Создать
    </BasePrimaryButton>
  </BaseNav>

  Props:
  - title: заголовок страницы;
  - title-prefix slot: компактный индикатор перед заголовком;
  - badge: компактная подпись рядом с заголовком { text, color?, bg? };
  - badges: несколько компактных подписей рядом с заголовком;
  - show-back: показывает кнопку возврата, по умолчанию true;
  - back-fallback: маршрут, куда вернуться, если в истории нет подходящей страницы;
  - back-fallback-pattern: RegExp для проверки подходящей предыдущей страницы;
  - back-handler: особая функция возврата для страницы;
  - heading-level: уровень заголовка, h2 по умолчанию;
  - title-size: точный размер заголовка для страниц со своим макетом;
  - back-label-override: явный текст ссылки возврата вместо «Назад».
-->
<template>
  <div class="base-nav" :class="{ 'base-nav--with-back': showBack }">
    <button
      v-if="showBack"
      class="base-nav-back"
      :aria-label="effectiveBackLabel"
      @click="handleBack"
    >
      <AppIcon class="base-nav-back__icon" name="chevronLeft" :size="14" />
      {{ effectiveBackLabel }}
    </button>

    <div class="base-nav-title-wrap">
      <component
        :is="headingTag"
        class="base-nav-title"
        :class="{ 'base-nav-title--with-prefix': $slots['title-prefix'] }"
        :style="titleStyles"
      >
        <slot name="title-prefix" />
        {{ title }}
      </component>
      <div v-if="navBadges.length" class="base-nav-badges">
        <span
          v-for="(navBadge, index) in navBadges"
          :key="`${navBadge.text}-${index}`"
          class="base-nav-badge"
          :style="{
            background: navBadge.bg || '#f3e8ff',
            color: navBadge.color || '#7c3aed',
          }"
        >
          {{ navBadge.text }}
        </span>
      </div>
    </div>

    <!-- Действия страницы: на мобильных переносятся в равномерную сетку. -->
    <div v-if="$slots.default" class="base-nav-actions">
      <slot />
    </div>
  </div>
</template>

<script>
import AppIcon from "@/components/Icon/AppIcon.vue";
import { goBackOrFallback } from "@/utils/routeUtils";

export default {
  name: "BaseNav",

  components: {
    AppIcon,
  },

  props: {
    title: {
      type: String,
      default: "",
    },
    badge: {
      // { text: string, color?: string, bg?: string }
      type: Object,
      default: null,
    },
    badges: {
      // [{ text: string, color?: string, bg?: string }]
      type: Array,
      default: () => [],
    },
    showBack: {
      type: Boolean,
      default: true,
    },
    backFallback: {
      type: String,
      default: "Home",
    },
    headingLevel: {
      type: Number,
      default: 2,
      validator: (v) => [2, 3].includes(v),
    },
    titleSize: {
      type: [Number, String],
      default: null,
    },
    backLabelOverride: {
      type: String,
      default: "",
    },
    backFallbackPattern: {
      // RegExp для goBackOrFallback, опционально
      type: RegExp,
      default: null,
    },
    backHandler: {
      // Кастомный обработчик возврата для страниц со специальной логикой
      type: Function,
      default: null,
    },
  },

  computed: {
    headingTag() {
      return `h${this.headingLevel}`;
    },

    hasPreviousRoute() {
      return Boolean(this.$router.options.history.state.back);
    },

    effectiveBackLabel() {
      return this.backLabelOverride
        || (this.hasPreviousRoute ? "Назад" : "На главную");
    },

    titleStyles() {
      if (!this.titleSize) return null;
      const size = typeof this.titleSize === "number"
        ? `${this.titleSize}px`
        : this.titleSize;
      return { fontSize: size };
    },

    navBadges() {
      if (this.badges.length) return this.badges;
      return this.badge ? [this.badge] : [];
    },
  },

  methods: {
    handleBack() {
      if (this.backHandler) {
        this.backHandler();
        return;
      }

      goBackOrFallback(
        this.$router,
        this.backFallback,
        this.backFallbackPattern
      );
    },
  },
};
</script>

<style scoped>
.base-nav {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
  flex-shrink: 0;
}

.base-nav-title-wrap {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
  margin-right: auto;
}

.base-nav-title {
  line-height: 1.2;
}

.base-nav-title--with-prefix {
  display: flex;
  align-items: center;
  gap: 6px;
}

.base-nav-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.base-nav-badges {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.base-nav-back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  padding: 0;
  font-size: 13px;
  line-height: 1;
  background: none;
  border: 0;
  color: #6b7280;
  cursor: pointer;
  flex-shrink: 0;
  transition: color 0.2s linear;
}

.base-nav-back:hover {
  color: var(--color-dark);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.base-nav-back__icon {
  color: #6b7280;
  transition: color 0.2s linear;
}

.base-nav-back:hover .base-nav-back__icon {
  color: var(--color-dark);
}

.base-nav-actions :deep(.btn) {
  flex-shrink: 0;
}

.base-nav-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.base-nav--with-back {
  display: grid;
  grid-template-areas:
    "back actions"
    "title actions";
  grid-template-columns: minmax(0, 1fr) auto;
  column-gap: 15px;
  row-gap: 8px;
}

.base-nav--with-back .base-nav-back {
  grid-area: back;
  justify-self: start;
}

.base-nav--with-back .base-nav-title-wrap {
  grid-area: title;
}

.base-nav--with-back .base-nav-actions {
  grid-area: actions;
  align-self: end;
}

@media (max-width: 768px) {
  .base-nav {
    flex-direction: column;
    align-items: flex-start;
  }

  .base-nav--with-back {
    grid-template-areas:
      "back"
      "title"
      "actions";
    grid-template-columns: minmax(0, 1fr);
    width: 100%;
    row-gap: 10px;
  }

  .base-nav-actions {
    flex-wrap: wrap;
    width: 100%;
  }

  .base-nav-actions :deep(.btn) {
    flex: 1 1 calc(50% - 5px);
    justify-content: center;
  }
}

</style>
